import React, { useState, useEffect, useRef } from 'react';
import { Button, Tooltip } from 'antd';
import { SoundOutlined, PauseCircleOutlined } from '@ant-design/icons';
import { authAxios } from '../../auth';
import { API } from '../../config';

interface AudioPlayButtonProps {
    language: string;  // 'greek' or 'hebrew'
    word: string;      // The actual Greek/Hebrew word
    size?: 'small' | 'middle' | 'large';
}

export const AudioPlayButton: React.FC<AudioPlayButtonProps> = ({
    language,
    word,
    size = 'small'
}) => {
    const [hasAudio, setHasAudio] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [audioBlobUrl, setAudioBlobUrl] = useState<string | null>(null);
    const audioRef = useRef<HTMLAudioElement | null>(null);

    // Check if audio exists when component mounts or language/word changes
    useEffect(() => {
        setIsLoading(true);
        setHasAudio(false);
        setAudioBlobUrl(null);

        authAxios.head(API.audioCheck(language, word))
            .then(() => setHasAudio(true))
            .catch(() => setHasAudio(false))
            .finally(() => setIsLoading(false));

        // Cleanup on unmount or language/word change
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
            if (audioBlobUrl) {
                URL.revokeObjectURL(audioBlobUrl);
            }
        };
    }, [language, word]);

    const handlePlay = async (e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent triggering word click

        if (isPlaying && audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            setIsPlaying(false);
        } else {
            try {
                // Fetch audio with auth header and create blob URL
                let blobUrl = audioBlobUrl;
                if (!blobUrl) {
                    const response = await authAxios.get(API.audioGet(language, word), {
                        responseType: 'blob'
                    });
                    blobUrl = URL.createObjectURL(response.data);
                    setAudioBlobUrl(blobUrl);
                }

                const audio = new Audio(blobUrl);
                audio.onended = () => setIsPlaying(false);
                audio.onerror = () => setIsPlaying(false);
                audio.play().catch(() => setIsPlaying(false));
                audioRef.current = audio;
                setIsPlaying(true);
            } catch {
                setIsPlaying(false);
            }
        }
    };

    // Don't render anything if loading or no audio
    if (isLoading || !hasAudio) {
        return null;
    }

    return (
        <Tooltip title={isPlaying ? 'Stop' : 'Play pronunciation'}>
            <Button
                type="text"
                size={size}
                icon={isPlaying ? <PauseCircleOutlined /> : <SoundOutlined />}
                onClick={handlePlay}
                style={{ marginLeft: 4 }}
            />
        </Tooltip>
    );
};
